import { Typography } from "components/parts/Typography/Typography";
import { newColors } from "constants/newColors";
import { DisplayInterview } from "pages/InterviewManagement/hook";
import { TabPanel } from "pages/InterviewManagement/InterviewList/TabPanel";
import React from "react";
import { InterviewType, Monitor } from "types/graphqlapi";
import { getDisplayYMD, isOnGoing, isToday } from "utils/common";
import {
  StyledInterview,
  StyledInterviewList,
  StyledInterviewTitle,
  StyledNodataTypography,
  StyledTab,
  StyledTabs,
  StyledMonitorNameArea,
  StyledTodayIndicator,
  StyledTypography,
  StyledInterviewSeq,
  StyledOnGoingIndicator,
  StyledMemoTypography,
  StyledMonitorNameTypography,
} from "./styled";
import { useConsoleState } from "providers/ConsoleStateProvider";

type InterviewListProps = {
  interviewList: DisplayInterview[];
  selectedInterviewDetail: DisplayInterview | undefined;
  selectInterviewDetail: (interview: DisplayInterview | undefined) => void;
};

export const InterviewList: React.FC<InterviewListProps> = ({
  interviewList,
  selectedInterviewDetail,
  selectInterviewDetail,
}) => {
  const { selectedSubjectType } = useConsoleState();
  const [tab, setTab] = React.useState(0);
  const handleChangeTab = (event: React.SyntheticEvent, newTab: number) => {
    setTab(newTab);
  };

  const rerunMonitorName = (monitorList: Monitor[]) => {
    switch (monitorList.length) {
      case 0:
        return <Typography text={"未アサイン"} size="small" />;
      case 1:
        return (
          <StyledMonitorNameTypography
            text={`${monitorList[0].name || "未設定"}(${
              monitorList[0].classification || "グループ / 割付なし"
            })`}
            size="small"
            interviewType={selectedSubjectType}
          />
        );
      default: {
        const uniqueMonitorClassificationString = Array.from(
          new Set(
            monitorList.map(
              (monitor) => monitor.classification || "グループ / 割付なし"
            )
          )
        ).join(" | ");

        return (
          <StyledMonitorNameTypography
            key={monitorList[0].id}
            text={uniqueMonitorClassificationString}
            size="small"
            interviewType={selectedSubjectType}
          />
        );
      }
    }
  };

  const returnInterviewList = (interviewList: DisplayInterview[]) =>
    interviewList.length === 0 ? (
      <StyledNodataTypography text="実査がありません" />
    ) : (
      interviewList.map((interview) => (
        <StyledInterview
          key={interview.id}
          onClick={() => {
            selectInterviewDetail(interview);
            window.history.replaceState({}, "", `${interview.id}`);
          }}
          style={
            selectedInterviewDetail?.id === interview.id
              ? { background: newColors.selected, cursor: "default" }
              : {}
          }
        >
          <StyledInterviewSeq
            text={`${interview.interview_seq}`}
            textAlign="center"
          />
          <StyledInterviewTitle>
            <Typography
              text={
                selectedSubjectType === InterviewType.TERM
                  ? `${
                      interview.scheduled_start_date &&
                      interview.scheduled_end_date
                        ? `${getDisplayYMD(
                            new Date(interview.scheduled_start_date)
                          )} ${
                            interview.scheduled_start_time
                          } ～ ${getDisplayYMD(
                            new Date(interview.scheduled_end_date)
                          )} ${interview.scheduled_end_time}`
                        : ""
                    }`
                  : `${
                      interview.scheduled_start_date
                        ? `${getDisplayYMD(
                            new Date(interview.scheduled_start_date)
                          )} ${interview.scheduled_start_time}～${
                            interview.scheduled_end_time
                          }`
                        : ""
                    }`
              }
            />
            <StyledMonitorNameArea>
              {rerunMonitorName(interview.monitorList)}
            </StyledMonitorNameArea>
            <StyledMemoTypography
              interviewType={selectedSubjectType}
              text={interview.memo ? interview.memo : ""}
              size="small"
            />
          </StyledInterviewTitle>
          {interview.scheduled_start_date &&
            interview.scheduled_end_date &&
            (selectedSubjectType === InterviewType.TERM ? (
              <StyledOnGoingIndicator>
                {isOnGoing(
                  interview.scheduled_start_date,
                  interview.scheduled_end_date,
                  interview.actual_end_date
                ) && <StyledTypography text={"実施期間中"} size="xsmall" />}
              </StyledOnGoingIndicator>
            ) : (
              <StyledTodayIndicator>
                {isToday(interview.scheduled_start_date) && (
                  <StyledTypography text={"今日"} size="xsmall" />
                )}
              </StyledTodayIndicator>
            ))}
        </StyledInterview>
      ))
    );

  const todayInterviewList = interviewList.filter(
    (interview) =>
      interview.scheduled_start_date && isToday(interview.scheduled_start_date)
  );

  const onGoingInterviewList = interviewList.filter((interview) =>
    isOnGoing(
      interview.scheduled_start_date,
      interview.scheduled_end_date,
      interview.actual_end_date
    )
  );

  const notAssignedInterviewList = interviewList.filter(
    (interview) =>
      interview.status !== "DONE" && interview.monitorList.length === 0
  );
  const doneInterviewList = interviewList.filter(
    (interview) => interview.status === "DONE"
  );

  return (
    <>
      <StyledInterviewList>
        <StyledTabs value={tab} onChange={handleChangeTab}>
          {[
            `すべて(${interviewList.length})`,
            selectedSubjectType === InterviewType.TERM
              ? `実施期間中(${onGoingInterviewList.length})`
              : `今日(${todayInterviewList.length})`,
            `未アサイン(${notAssignedInterviewList.length})`,
            `実施済(${doneInterviewList.length})`,
          ].map((tab, index) => (
            <StyledTab
              aria-selected
              key={index}
              label={tab}
              {...{
                id: `interview-tab-${index}`,
                "aria-controls": `interview-tabpanel-${index}`,
              }}
            />
          ))}
        </StyledTabs>
        {[
          interviewList,
          selectedSubjectType === InterviewType.TERM
            ? onGoingInterviewList
            : todayInterviewList,
          notAssignedInterviewList,
          doneInterviewList,
        ].map((interviewList, index) => (
          <TabPanel key={index} value={tab} index={index}>
            {returnInterviewList(interviewList)}
          </TabPanel>
        ))}
      </StyledInterviewList>
    </>
  );
};
